import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '../stores/authStore'

// Use env variable for API URL (falls back to relative path for production)
const API_URL = import.meta.env.VITE_API_URL || ''
const API_BASE = `${API_URL}/api/v1`

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config

    if (!originalRequest) {
      return Promise.reject(error)
    }

    // If 401 and we have a refresh token, try to refresh
    if (error.response?.status === 401) {
      const refreshToken = useAuthStore.getState().refreshToken

      if (refreshToken && !(originalRequest as unknown as { _retry?: boolean })._retry) {
        ;(originalRequest as unknown as { _retry?: boolean })._retry = true

        try {
          const response = await axios.post(`${API_BASE}/auth/refresh`, {
            refresh_token: refreshToken,
          })

          const { access_token, refresh_token } = response.data
          useAuthStore.getState().updateTokens(access_token, refresh_token)

          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${access_token}`
          return api(originalRequest)
        } catch {
          // Refresh failed - logout
          useAuthStore.getState().logout()
          window.location.href = '/login'
        }
      } else {
        // No refresh token or retry failed
        useAuthStore.getState().logout()
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

export default api
